import {AvalancheReelController} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheReelController";
import {FITHAvalancheReelPartAnimator} from "./FITHAvalancheReelPartAnimator";
import {ReelAnimationState} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelAnimationState";
import {FITHGameConfig} from "../FITHGameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {FITHGameAssets} from "../FITHGameAssets";
import {IParsedStackedSymbolName, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {FITHParsedGameData} from "../server/data/FITHParsedGameData";
import {FITHParsedInitData} from "../server/data/FITHParsedInitData";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {
    IReelStartSpinData,
    IReelStopSpinData
} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {TimelineLite, TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {FITHGameModuleConfig} from "../FITHGameModuleConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";

// this class is working as per avalanche in main game and as per SpinReelController in bonus game.
export class FITHAvalancheReelController extends AvalancheReelController {
    protected _stopCounter: number = 0;
    protected _addedExtraStoppingFullStackedSymbolNum: number;
    protected _spinStopOffset: number;
    protected _currentFullReelSet: string[];
    protected _nextFullReelSet: string[];
    private _leftIndexes: number[] = [];
    private _rightIndexes: number[] = [];
    private _bottomIndex: number = -1;
    private _topIndex: number = -1;
    private _leftIndexesAfterCollapse: number[] = [];
    private _rightIndexesAfterCollapse: number[] = [];
    private _topIndexAfterCollapse: number = -1;
    private _fsCount: number;

    // #255
    private _isRestore: boolean = false;

    public get currentSpinStepLeftTime(): number {
        return (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).currentStepLeftTime;
    }

    public reMakeDAsBigSymbol(): void {
        let sym: StateReelSymbol,
            left: PIXI.Sprite,
            right: PIXI.Sprite,
            top: PIXI.Sprite;

        if (this._leftIndexesAfterCollapse.length > 0 && this._leftIndexesAfterCollapse[0] !== this._leftIndexes[0]) {
            for (let i: number = 0; i < this._leftIndexesAfterCollapse.length; i++) {
                if (this._leftIndexes.indexOf(this._leftIndexesAfterCollapse[i]) > -1) {
                    continue;
                }

                sym = Reels.getSymbol(this._reelId, this._leftIndexesAfterCollapse[i])!;
                left = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Left")[0]);
                left.anchor.set(0.5);
                sym.addChild(left);
            }
        }

        if (this._rightIndexesAfterCollapse.length > 0 && this._rightIndexesAfterCollapse[0] !== this._rightIndexes[0]) {
            for (let i: number = 0; i < this._rightIndexesAfterCollapse.length; i++) {
                if (this._rightIndexes.indexOf(this._rightIndexesAfterCollapse[i]) > -1) {
                    continue;
                }

                sym = Reels.getSymbol(this._reelId, this._rightIndexesAfterCollapse[i])!;
                right = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Right")[0]);
                right.anchor.set(0.5);
                sym.addChild(right);
            }
        }

        if (this._topIndexAfterCollapse !== -1 && this._topIndexAfterCollapse !== this._topIndex) {
            sym = Reels.getSymbol(this._reelId, this._topIndexAfterCollapse)!;
            top = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Top")[0]);
            top.anchor.set(0.5);
            sym.addChild(top);
        }
    }

    /**
     * @inheritDoc
     * @param data
     */
    public onGameDataParsed(data: FITHParsedGameData): void {
        super.onGameDataParsed(data);

        this.onData(data);
    }

    // The stop offset is for features on the reel to prolong the stop.
    // The reelArea will take the biggest offset as the real stop offset.
    public getSpinStopOffset(): number {
        if (this._currentState === ReelAnimationState.INACTIVE || this._currentState === ReelAnimationState.IDLE) {
            return 0;
        }

        // check if there is unfinished stacked wilds in the bottom of the stop reel set.
        // When the offset is more than 0, we have to make sure to compensate it from the stop reel set.
        let spinStopOffset: number = 0;
        let bottomSymbol: string = this._stopReelSet[this._stopReelSet.length - 1];
        let parsedStackedName: IParsedStackedSymbolName = StateReelSymbol.parseStackedSymbolName(bottomSymbol, this._reelId);
        if (parsedStackedName.isStacked && parsedStackedName.index < (parsedStackedName.totalNum - 1)) {
            spinStopOffset = parsedStackedName.totalNum - 1 - parsedStackedName.index;
        }

        return Math.max(0, spinStopOffset - FITHGameConfig.instance.SYMBOLS_NUM_IN_REEL[4]);
    }

    public startSpin(data: IReelStartSpinData, previousDelay: number): number {
        const tempStopReelSet: string[] = [...this._stopReelSet],
            retVal: number = super.startSpin(data, previousDelay);

        this._stopReelSet = tempStopReelSet;

        (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).isLastFs = (this._fsCount - 1 === 0);

        return retVal;
    }

    public stopSpin(stopData: IReelStopSpinData, previousStopTime: number): number {
        if (this._gameMode === FITHGameMode.BONUS) {
            if (!FITHGameConfig.instance.BONUS_GAME_TRAIN_STOPPED) {
                return previousStopTime;
            }

            this._avalancheReelPartAnimator.isQuickStop = stopData.quickStop;
            this._avalancheReelPartAnimator.isFastSpin = stopData.fastSpin;
            this._spinStopOffset = stopData.spinStopOffset;

            // Quick stop stopping reel
            if (this.currentState === ReelAnimationState.STOP_SPIN) {
                return (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).getStopTime(this._stopCounter - 1) + this.currentSpinStepLeftTime;
            }

            // Don't need to restore spin speed if quick stop near win?
            if (stopData.active) {
                const stopTimeTotal: number = (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).getStopTime(this._symNumTotal + this._spinStopOffset - 1);
                if (stopData.quickStop) {
                    this._addedStopDelay = 0;
                }
                let stopTimerDelay: number = Math.max(0, previousStopTime + this.getStopDelayDelta(stopData) + this._addedStopDelay - stopTimeTotal);
                this._addedStopDelay = 0;

                // #200
                if (stopData.quickStop) {
                    stopTimerDelay = 0;
                }

                if (this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
                    // quick stop.
                    const timelineLeftTime: number = this._reelStopDelayTimer.totalDuration() - this._reelStopDelayTimer.time();
                    if (stopTimerDelay >= timelineLeftTime) {
                        return timelineLeftTime + stopTimeTotal;
                    }
                    this._reelStopDelayTimer.pause();
                    this._reelStopDelayTimer.kill();
                }
                this._reelStopDelayTimer = TweenLite.to(this, stopTimerDelay, {onComplete: () => this.clearStopDelay()});

                // #200
                if (stopData.quickStop) {
                    return 0.0001;
                }

                return stopTimerDelay + stopTimeTotal + this.currentSpinStepLeftTime;
            } else {
                // If reel not active we fake a spin stopped so it actually send the event.
                Logger.logDev(`ReelController.stopSpin():Inactive reel ${this._reelId} stop spin animation completed!`);
                EventHandler.dispatchEvent(new GameEvent(ReelEvent.REEL_STOP_SPIN_ANIMATION_COMPLETE,
                    {
                        reelId: this._reelId,
                        fastSpin: this._avalancheReelPartAnimator.isFastSpin,
                        quickStop: this._avalancheReelPartAnimator.isQuickStop,
                        active: false
                    }
                ));

                return previousStopTime;
            }
        } else {
            return super.stopSpin(stopData, previousStopTime);
        }
    }

    public enterFS(): void {
        (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).enterFS();
    }

    public exitFS(): void {
        (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).exitFS();

        this._gameMode = FITHGameMode.NORMAL;
    }

    /**
     * @description To re-order the symbols.
     */
    public reOrderSymbols(): void {
        const allSymbols: StateReelSymbol[] = this.allSymbols;
        let sym: StateReelSymbol;

        // re-ordering symbols
        for (let i: number = 0; i < allSymbols.length; i++) {
            sym = allSymbols[i];
            sym.parent.addChild(sym);
        }
    }

    /**
     * @inheritDoc
     * @param data
     */
    protected onInitDataParsed(data: FITHParsedInitData): void {
        super.onInitDataParsed(data);

        // #255
        this._isRestore = data.isRestoreState;

        this.onData(data);

        // #144, re-calculating allWinPositions
        this._allWinPositions = ArrayHelper.initArrayWithValues(data.activeRows, (index:number) => (this._reelsAfterCollapes[index] === ReelSymbolName.EMPTY));
        for (let i: number = 0; i < (this._symNumTotal - data.activeRows); i++) {
            this._allWinPositions.push(false);
        }

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    /**
     * @inheritDoc
     * @override To re-order the symbols.
     */
    protected initReelStrip(): void {
        super.initReelStrip();
        if (!this._reelInitialized) {
            return;
        }

        this.reOrderSymbols();

        if (!this._isRestore && this._gameMode === FITHGameMode.NORMAL && this._reelId < FITHGameConfig.instance.REELS_NUM_ORIGINAL) {
            this.makeDSymbolABigSymbol();

            this._isRestore = true;
        }
    }

    /**
     * @inheritDoc
     * @override To set the state in reel part animator.
     */
    protected onStopDelayDone(): void {
        if (this._gameMode === FITHGameMode.BONUS) {
            this.onStopDelayDoneBonusGame();
        } else {
            if (this.isAvalanche()) {
                (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).currentState = ReelAnimationState.AVALANCHE_NEW;
            } else {
                (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).currentState = ReelAnimationState.ALL_IN;
            }

            super.onStopDelayDone();
        }
    }

    /**
     * @inheritDoc
     * @override To modify nearWin delay.
     */
    protected getFakeStopDelay(): number {
        // not calling super method

        if (this.reelId === 0) {
            return 0.01;
        }
        if (this._reelPartAnimator.isQuickStop) {
            return this.getQuickStopDelay(this.reelId);
        }
        // change is here from super.
        return 0; // provide no delay from here for nearWin, controlling it from FITHAvalancheReelPartAnimator
    }

    /**
     * @inheritDoc
     * @override To set the state in reel part animator.
     */
    protected completeSpin(): void {
        (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).currentState = this._currentState;

        super.completeSpin();
    }

    /**
     * @inheritDoc
     * @override To re-order the symbols and apply the overlay layers on D symbols for ALL_IN state only.
     */
    protected onSpinStopStarted(): void {
        this.reOrderSymbols();

        // used in bonus game only.
        this._isSpinBlurred = false;

        super.onSpinStopStarted();

        if (this._currentState !== ReelAnimationState.ALL_IN) {
            return;
        }

        this.makeDSymbolABigSymbol();
    }

    protected startSpinAnimation(data: IReelStartSpinData, previousDelay: number): number {
        if (this._gameMode === FITHGameMode.BONUS) {
            this._isSpinBlurred = true;
            const delay = previousDelay + (this.reelId === 0 ? 0 : (data.fastSpin ? 0.025 : 0.05));
            this._currentState = ReelAnimationState.START_SPIN;
            const timeline: TimelineLite = new TimelineLite();
            timeline.add((this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).getStartSpinAnimate(delay, this.createNextSymbol()));
            timeline.add(() => this.onSpinStartedCompleted());
            return delay;
        } else {
            return super.startSpinAnimation(data, previousDelay);
        }
    }

    protected onSpinStartedCompleted(): void {
        if (this._gameMode === FITHGameMode.BONUS) {
            this.updateStackedSymbols(this.allSymbols[this.allSymbols.length - 1], null, 1, false);
            this.removeSymbol(this.allSymbols.pop()!);
            this._currentState = ReelAnimationState.SPIN;
            this.spinSpinAnimation();
            EventHandler.dispatchEvent(new GameEvent(ReelEvent.REEL_SPIN_STARTED, this._reelId));
        } else {
            super.onSpinStartedCompleted();
        }
    }

    protected onSpinStepCompleted(): void {
        this.updateStackedSymbols(this.allSymbols[this.allSymbols.length - 2], null, 1, false);
        this.removeSymbol(this.allSymbols.pop()!);
        switch (this._currentState) {
            case ReelAnimationState.SPIN:
                this.spinSpinAnimation();
                return;
            case ReelAnimationState.STOP_SPIN:
                if (this._stopCounter > 1) {
                    this.spinSpinAnimation();
                    return;
                }
                const timeline: TimelineLite = new TimelineLite();
                timeline.add([
                    (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).getStopSpinAnimation(this.createNextSymbol()),
                    () => this.onSpinStopStarted()
                ]);
                timeline.add(() => this.onSpinStopAnimationCompleted());
                return;
            default:
                debugger;
                throw new Error(`SpinReelController.onSpinStepCompleted()`);
        }
    }

    // If a reel has special delay based on feature, override this method.
    protected getStopDelayDelta(stopSpinData: IReelStopSpinData): number {
        // #115
        if (stopSpinData.quickStop || stopSpinData.fastSpin) {
            if (FITHGameModuleConfig.spinController.getFirstActiveReelInBonus() === this._reelId) {
                return FITHGameConfig.instance.REEL_SPIN_START_SPEED_BONUS_GAME;
            } else {
                return 0.0001;
            }
        }

        // no need of near win stop delay delta in bonus game.

        return this.getNormalStopDelay(this.reelId);
    }

    // If there are unfinished stacked symbols, fill it up, this game has no stacked symbols.
    protected handleUnfinishedSpinningStackedSymbols(): string[] {
        return [];
    }

    protected onSpinStopAnimationCompleted(): void {
        if (this._gameMode === FITHGameMode.BONUS) {
            this.removeSymbol(this.allSymbols.pop()!);
            this.updateStackedSymbols(this.allSymbols[this.allSymbols.length - 1], null, 1, false);
            super.onSpinStopAnimationCompleted();
        } else {
            super.onSpinStopAnimationCompleted();
        }
    }

    protected createNextSymbol(previousSymbol?: StateReelSymbol | null, init?: boolean): StateReelSymbol {
        if (this._stopCounter > 0) {
            // change the counter outside if{} so we don't put extra added symbols for the stop offset in the stop
            // symbols.
            this._stopCounter--;
        }
        const retVal: StateReelSymbol = super.createNextSymbol(previousSymbol, init);

        if (this._gameMode !== FITHGameMode.BONUS && (retVal.symName === "X")) {
            retVal.setVisibility(false);
        }

        if (this._gameMode === FITHGameMode.BONUS && !(retVal.symName === "V" || retVal.symName === "X")) {
            retVal.setVisibility(false);
        }
        return retVal;
    }

    protected moveReelSetPosition() {
        if (this._gameMode === FITHGameMode.BONUS) {
            if (this._currentReelSetPosition === 0) {
                if (this._currentFullReelSet === null) {
                    debugger;
                    throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
                }
                this._currentReelSet = this._currentFullReelSet.concat();
            }
            this._currentReelSetPosition--;
            this._currentReelSetPosition = ArrayHelper.reviseIndexInLoopRange(this._currentReelSet.length, this._currentReelSetPosition);
        } else {
            super.moveReelSetPosition();
        }
    }

    protected setStartSpinData(data: IReelStartSpinData) {
        let useOldGameMode: boolean = false,
            tempGameMode: string = "";

        // as spin starts late(after train stops) in this game's Bonus Game and train stops when game data has been parsed and so the below values gets altered.
        if (this._gameMode === FITHGameMode.BONUS && this._nextGameMode === FITHGameMode.NORMAL) {
            useOldGameMode = true;
            tempGameMode = this._gameMode;
        }
        super.setStartSpinData(data);

        // used in bonus game only.
        if (useOldGameMode) {
            this._gameMode = tempGameMode;
        }
        this._addedExtraStoppingFullStackedSymbolNum = 0;
        this._currentFullReelSet = this._nextFullReelSet.concat();
    }

    protected getNormalStopDelay(reelId: number): number {
        return (this._gameMode === FITHGameMode.BONUS) ? FITHGameConfig.instance.REEL_STOP_DELAY_BONUS_GAME[reelId] : super.getNormalStopDelay(reelId);
    }

    private onData(data: FITHParsedInitData | FITHParsedGameData): void {
        const originalReelsNum: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;

        this._leftIndexes = [];
        this._rightIndexes = [];
        this._bottomIndex = -1;
        this._topIndex = -1;

        // used in bonus game only.
        this._nextFullReelSet = Helper.getReelSet(data.nextReelSetName, this._reelId);
        (this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).mode = data.mode;
        this._fsCount = data.freespinsLeft;

        if (this._reelId > (originalReelsNum - 1)) {
            return;
        }

        if (this._reelId === 0) {
            this.calculateLeftIndexes(["", "", "", "", "", ""]);
        } else {
            this.calculateLeftIndexes(data.reels[this._reelId - 1]);
        }

        if (this._reelId === (originalReelsNum - 1)) {
            this.calculateRightIndexes(["", "", "", "", "", ""]);
        } else {
            this.calculateRightIndexes(data.reels[this._reelId + 1]);
        }

        this.calculateBottomIndexes();

        this.calculateTopIndexes();

        if (data instanceof FITHParsedGameData) {
            this.reCalculateAfterCollapse(data.reelsAfterCollapse);
        }
    }

    private calculateLeftIndexes(previousReelStopReelSet: string[]): void {
        const reels: string[] = this._stopReelSet;

        for (let i: number = 1; i < reels.length; i++) {
            if (previousReelStopReelSet[i] !== "D" && reels[i] === "D") {
                this._leftIndexes.push(i);
            }
        }
    }

    private calculateRightIndexes(nextReelStopReelSet: string[]): void {
        const reels: string[] = this._stopReelSet;

        for (let i: number = 1; i < reels.length; i++) {
            if (reels[i] === "D" && nextReelStopReelSet[i] !== "D") {
                this._rightIndexes.push(i);
            }
        }
    }

    private calculateBottomIndexes(): void {
        const reels: string[] = this._stopReelSet;

        if (reels[reels.length - 1] === "D" || reels[reels.length - 1] === "X") {
            this._bottomIndex = (reels.length - 1);
        }
    }

    private calculateTopIndexes(): void {
        const reels: string[] = this._stopReelSet;

        for (let i: number = 1; i < reels.length; i++) {
            if (reels[i - 1] !== "D" && reels[i] === "D") {
                this._topIndex = i;
                break;
            }
        }
    }

    private reCalculateAfterCollapse(reelsAfterCollapse: string[][]): void {
        const symsNumInReel: number = FITHGameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._leftIndexesAfterCollapse = [];
        this._rightIndexesAfterCollapse = [];

        this._topIndexAfterCollapse = -1;

        for (let i: number = this._topIndex; i < symsNumInReel; i++) {
            if (reelsAfterCollapse[this._reelId][i] === "D") {
                this._topIndexAfterCollapse = i;
                break;
            }
        }

        if (this._topIndexAfterCollapse !== -1) {
            if (this._reelId !== 0) {
                for (let i: number = this._topIndexAfterCollapse; i < symsNumInReel; i++) {
                    if (reelsAfterCollapse[this._reelId - 1][i] !== "D") {
                        this._leftIndexesAfterCollapse.push(i);
                    }
                }
            }

            if (this._reelId !== (FITHGameConfig.instance.REELS_NUM_ORIGINAL - 1)) {
                for (let i: number = this._topIndexAfterCollapse; i < symsNumInReel; i++) {
                    if (reelsAfterCollapse[this._reelId + 1][i] !== "D") {
                        this._rightIndexesAfterCollapse.push(i);
                    }
                }
            }
        }
    }

    /**
     * @description To apply the overlay layers on D symbols for ALL_IN state only.
     */
    private makeDSymbolABigSymbol(): void {
        let sym: StateReelSymbol,
            left: PIXI.Sprite,
            right: PIXI.Sprite,
            bottom: PIXI.Sprite,
            top: PIXI.Sprite;

        for (let i: number = 0; i < this._leftIndexes.length; i++) {
            sym = Reels.getSymbol(this._reelId, this._leftIndexes[i])!;
            left = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Left")[0]);
            left.anchor.set(0.5);
            sym.addChild(left);
        }

        for (let i: number = 0; i < this._rightIndexes.length; i++) {
            sym = Reels.getSymbol(this._reelId, this._rightIndexes[i])!;
            right = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Right")[0]);
            right.anchor.set(0.5);
            sym.addChild(right);
        }

        if (this._bottomIndex !== -1) {
            sym = Reels.getSymbol(this._reelId, this._bottomIndex)!;
            // we may place bottom symbol with X symbols
            bottom = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath("D") + "_Bottom")[0]);
            bottom.anchor.set(0.5);
            sym.addChild(bottom);
        }

        if (this._topIndex !== -1) {
            sym = Reels.getSymbol(this._reelId, this._topIndex)!;
            top = new PIXI.Sprite(GameResources.getTextures(FITHGameAssets.getSymbolStateTexturePath(sym.symName) + "_Top")[0]);
            top.anchor.set(0.5);
            sym.addChild(top);
        }
    }

    private spinSpinAnimation(): void {
        const timeline: TimelineLite = new TimelineLite();
        timeline.add((this._avalancheReelPartAnimator as FITHAvalancheReelPartAnimator).getSpinAnimation(this.createNextSymbol()));
        timeline.add(() => this.onSpinStepCompleted());
    }

    private onStopDelayDoneBonusGame(): void {
        const unfinishedSpinningStackedSymbolFillUp: string[] = this.handleUnfinishedSpinningStackedSymbols();
        this._currentReelSet = this.makeUpStopReelSet(FITHGameConfig.instance.SYMBOLS_NUM_IN_REEL[4] + this._spinStopOffset - unfinishedSpinningStackedSymbolFillUp.length, false).concat(unfinishedSpinningStackedSymbolFillUp);
        this._currentReelSetPosition = this._currentReelSet.length;

        if (this._addedExtraStoppingFullStackedSymbolNum > 0) {
            // To add delay on the rest of reels. if there is any slow motion, it will be calculated in checkSlowMotionStopDelay().
            this._spinStopOffset += this._addedExtraStoppingFullStackedSymbolNum;
        }
        this._stopCounter = this._symNumTotal + this._spinStopOffset;
        this._currentState = ReelAnimationState.STOP_SPIN;
    }
}